import {
  CircleStopIcon,
  CopyIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDuplicateCampaign } from '@/modules/campaigns/hooks/useDuplicateCampaign';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { Campaign } from '@/shared/types/campaigns';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

interface ActionsCellProps {
  onDeleteClick: (campaignId: string) => void;
  onStopClick: (campaignId: string) => void;
  item: Campaign;
}

export const ActionsCell = ({ onDeleteClick, onStopClick, item }: ActionsCellProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.WRITE)
  );

  const { duplicateCampaign, isDuplicating } = useDuplicateCampaign();

  const navigateToSetup = (campaignId: string) => {
    navigate(`/${slug}/campaigns/${campaignId}/setup`);
  };

  const handleEdit = () => {
    trackEvent(EventName.ClickEditCampaign, { campaign_id: item.campaign_id });
    navigateToSetup(item.campaign_id);
  };

  const handleDuplicate = async () => {
    try {
      await duplicateCampaign(item, (newCampaign) => navigateToSetup(newCampaign.campaign_id));
    } catch {
      // createCampaignAsync surfaces its own toast on error; nothing to do here.
    }
  };

  const handleStop = () => {
    trackEvent(EventName.ClickStopCampaign, { campaign_id: item.campaign_id });
    onStopClick(item.campaign_id);
  };

  const handleDelete = () => {
    trackEvent(EventName.ClickDeleteCampaign, { campaign_id: item.campaign_id });
    onDeleteClick(item.campaign_id);
  };

  if (!canWrite) {
    return null;
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          variant="ghost-neutral"
          size="icon-sm"
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {item.status === 'in_progress' && (
          <DropdownMenuItem onSelect={handleStop} className="flex items-center gap-2">
            <CircleStopIcon size={16} className="text-muted-foreground" />
            {`${t('campaigns.stop')}…`}
          </DropdownMenuItem>
        )}

        {(item.status === 'draft' || item.status === 'ready' || item.status === 'in_progress') && (
          <DropdownMenuItem onSelect={handleEdit} className="flex items-center gap-2">
            <PencilIcon size={16} className="text-muted-foreground" />
            {`${t('common.edit')}…`}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onSelect={handleDuplicate}
          disabled={isDuplicating}
          className="flex items-center gap-2"
        >
          <CopyIcon size={16} className="text-muted-foreground" />
          {`${t('campaigns.duplicate')}…`}
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={handleDelete}
          variant="destructive"
          className="flex items-center gap-2"
        >
          <Trash2Icon size={16} />
          {`${t('common.delete')}…`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
